import { useState, useEffect, useCallback, useRef } from "react";

const QUEUE_KEY = "omni_offline_queue";

function loadQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch { return []; }
}

function persistQueue(q) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

export function useOfflineQueue({ onSend, onStatusChange }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState(loadQueue);
  const [flushing, setFlushing] = useState(false);
  const flushingRef = useRef(false);
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Flush queue when back online
  useEffect(() => {
    if (isOnline && queue.length > 0 && !flushingRef.current) {
      flushQueue();
    }
  }, [isOnline]);

  const flushQueue = useCallback(async () => {
    if (flushingRef.current) return;
    flushingRef.current = true;
    setFlushing(true);
    const pending = loadQueue();
    const failed = [];

    for (const item of pending) {
      try {
        await onSendRef.current(item);
        onStatusChange?.("sent", item);
      } catch {
        failed.push(item);
      }
    }

    persistQueue(failed);
    setQueue(failed);
    flushingRef.current = false;
    setFlushing(false);

    if (failed.length === 0 && pending.length > 0) {
      onStatusChange?.("flushed", null);
    }
  }, [onStatusChange]);

  const enqueue = useCallback((item) => {
    const updated = [...loadQueue(), { ...item, queued_at: Date.now() }];
    persistQueue(updated);
    setQueue(updated);
  }, []);

  const clearQueue = useCallback(() => {
    persistQueue([]);
    setQueue([]);
  }, []);

  return { isOnline, queue, flushing, enqueue, clearQueue, flushQueue };
}
